import { TrashIcon } from "lucide-react";
import { PopoverContent } from "../ui/popover";
import { useState } from "react";
import { z } from "zod";
import { tableValidator } from "../../models/schemasV2";
import { Input } from "../ui/input";
import { PopoverClose } from "@radix-ui/react-popover";
import { Button } from "../ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { HandleTableActionParams } from "../../hooks/templateV2/types";
import { Textarea } from "../ui/textarea";

interface TablePopoverProps {
  table?: z.infer<typeof tableValidator>;
  deleteTable: (tableId: string) => void;
  updateTable: (params: HandleTableActionParams) => void;
}

export const TablePopover = ({
  table,
  deleteTable,
  updateTable,
}: TablePopoverProps) => {
  const [tableName, setTableName] = useState(table?.name || "");
  const [tableDescription, setTableDescription] = useState(
    table?.description || "",
  );
  const [rowKeyColumnId, setRowKeyColumnId] = useState(table?.rowKeyColumnId);

  return (
    <PopoverContent align="start" className="w-[500px]">
      {table && (
        <PopoverClose asChild>
          <div className="flex justify-end">
            <TrashIcon
              className="cursor-pointer"
              onClick={() => deleteTable(table?.id || "")}
              size={16}
            />
          </div>
        </PopoverClose>
      )}
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <label htmlFor="tableName" className="text-sm">
            Table Name
          </label>
          <Input
            placeholder="Table Name"
            type="text"
            id="tableName"
            onChange={(e) => setTableName(e.target.value)}
            value={tableName}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="description" className="text-sm">
            AI Instruction (Optional)
          </label>
          <Textarea
            placeholder="AI Instruction"
            id="aiInstruction"
            onChange={(e) => setTableDescription(e.target.value)}
            value={tableDescription}
          />
        </div>
        <PopoverClose asChild>
          <Button
            className="mt-2"
            onClick={() =>
              updateTable({
                tableId: table?.id || "",
                tableName: tableName,
                description: tableDescription,
                rowKeyColumnId: rowKeyColumnId ? rowKeyColumnId : undefined,
              })
            }
          >
            Save
          </Button>
        </PopoverClose>
        <Accordion type="single" collapsible>
          <AccordionItem value="item-1">
            <AccordionTrigger className="text-sm font-normal">
              Advanced Settings
            </AccordionTrigger>
            <AccordionContent>
              <div className="flex gap-3">
                <div className="flex flex-col gap-1 p-4 rounded-md border border-stone-200 ">
                  <RadioGroup
                    value={rowKeyColumnId ? "row_key" : "unlimited"}
                    onValueChange={(value) =>
                      value === "unlimited"
                        ? setRowKeyColumnId(undefined)
                        : null
                    }
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="unlimited" id="unlimited" />
                      <label htmlFor="unlimited">Unlimited Rows</label>
                    </div>
                  </RadioGroup>
                  <div className="text-sm text-font-secondary">
                    No fixed row labels
                  </div>
                  <img
                    src="/inAppImages/unlimited_rows_image.svg"
                    alt="Unlimited Rows"
                    width={100}
                    height={100}
                  />
                </div>
                <div className="flex flex-col gap-1 p-4 rounded-md border border-stone-200 ">
                  <RadioGroup
                    value={rowKeyColumnId ? "row_key" : ""}
                    onValueChange={(value) =>
                      value === "row_key"
                        ? setRowKeyColumnId(
                            table?.rowKeyColumnId || crypto.randomUUID(),
                          )
                        : null
                    }
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="row_key" id="row_key" />
                      <label htmlFor="row_key">Preset Rows</label>
                    </div>
                  </RadioGroup>
                  <div className="text-sm text-font-secondary">
                    Predefined rows
                  </div>
                  <img
                    src="/inAppImages/row_key_image.svg"
                    alt="Unlimited Rows"
                    width={100}
                    height={100}
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </PopoverContent>
  );
};
